import React, { useEffect, useRef, useState } from "react";
import { withSnackbar } from "notistack";
import { Skeleton } from "@mui/material";

import Btn from "components/Custom/Btn";
import ExcelGrid from "components/Custom/ExcelGrid";
import TxtRow from "components/Custom/TxtRow";
import AutoComplete from "components/Custom/AutoComplete";
import UiAccordion from "components/Custom/UiAccordion";
import AddSalesInvoiceSkeleton from "./OrderSkeleton";

import { ERRORS, LABELS, SUCCESS, TITLES, WARNINGS } from "common/lables";
import { COMMON_JS } from "common/scripts";
import { DEFAULTS, NOTIFY } from "common/config";
import { COLORS } from "common/colors";
import { GEO_APIs } from "apihandlers/geography";
import { CUSTOMER_APIs } from "apihandlers/customer";
import { PRODUCT_APIs } from "apihandlers/product";
import { ORDER_APIs } from "apihandlers/order";
import { TRANS_APIs } from "apihandlers/transaction";

const AddSalesInvoice = (props: any) => {
  const { hideHeader, setPageMenu, setManageDialog } = props;

  const areasRef: any = useRef(null);
  const partyRef: any = useRef(null);
  const addSalesRef: any = useRef(null);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any>(null);
  const [filter, setFilter] = useState<any>(null);
  const [selectedRow, setSelectedRow] = useState<any>({});
  const [areas, setAreas] = useState<any>(null);
  const [products, setProducts] = useState<any>([]);
  const [parties, setParties] = useState<any>(null);
  const [stocks, setStocks] = useState<any>({});

  const [mandatoryResults, setMandatoryResults] = useState<any[]>([]);

  const formulaRenderer = (row) => {
    let quantity = Number(row["quantity"] || 0);
    let price = Number(row["price"] || 0);
    let taxStructure = row["taxName"];
    let basic = 0;
    let taxAmount = 0;
    let cgst = 0;
    let sgst = 0;
    let total = 0;
    if (!isNaN(quantity) && !isNaN(price)) {
      basic = price * quantity;
      taxAmount = basic * (taxStructure.replace("%", "") / 100);
      cgst = Number((taxAmount / 2).toFixed(2));
      sgst = Number((taxAmount / 2).toFixed(2));
      total = basic + taxAmount;
    }
    row["basic"] = basic;
    row["taxAmount"] = taxAmount;
    row["total"] = total;
    row["cgst"] = cgst;
    row["sgst"] = sgst;
    return row;
  };

  const columns = [
    {
      key: "slNo",
      name: LABELS.SL_NO,
      width: "5%",
      type: "number",
      editable: false,
      sortable: true,
      searchable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      key: "productName",
      type: "text",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.PRODUCT,
      width: "25%",
    },
    {
      key: "stockQty",
      type: "number",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.CLOSING_STOCK,
      width: "10%",
    },
    {
      key: "quantity",
      type: "number",
      editable: true,
      sortable: true,
      searchable: true,
      name: LABELS.QUANTITY,
      width: "10%",
      renderSum: true,
      renderSumPrefix: "Total Quantity:",
      formula: formulaRenderer,
      isDisabled: (row: any) => {
        return row.stockQty <= 0;
      },
    },
    {
      key: "price",
      type: "number",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.RATE,
      width: "10%",
      renderSum: true,
      formula: formulaRenderer,
    },
    {
      key: "basic",
      type: "number",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.BASIC_RATE,
      width: "10%",
      renderSum: true,
      renderSumPrefix: "Total Basic: ",
      formula: formulaRenderer,
    },
    {
      key: "cgst",
      type: "number",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.CGST_AMOUNT,
      width: "10%",
      renderSum: true,
      renderSumPrefix: "Total CGST : ",
      formula: formulaRenderer,
    },
    {
      key: "sgst",
      type: "number",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.SGST_AMOUNT,
      width: "10%",
      renderSum: true,
      renderSumPrefix: "Total SGST : ",
      formula: formulaRenderer,
    },
    {
      key: "total",
      type: "number",
      editable: false,
      sortable: true,
      searchable: true,
      name: LABELS.AMOUNT,
      width: "10%",
      renderSum: true,
      renderSumPrefix: "Total Amount: ",
      formula: formulaRenderer,
    },
  ];

  useEffect(() => {
    if (loading) {
      getStocks();
    }
  }, [loading]);

  const getStocks = () => {
    const args = {};
    TRANS_APIs.getStocks(args).then((res: any) => {
      const response: any = res.response;
      const { status } = res;
      if (status === 200) {
        let list = response.stockItems;
        setStocks(list);
        getRoute();
      }
    });
  };

  const getRoute = () => {
    const args = {
      activeStatus: true,
    };
    GEO_APIs.getAreas(args).then((res: any) => {
      const response: any = res.response;
      const { status } = res;
      if (status === 200) {
        let list = response.records.map((r) => {
          return {
            id: r.id,
            label: r.areaName,
          };
        });
        setAreas(list);
        getParty();
      }
    });
  };

  const getParty = (areaId = null) => {
    const args = {
      areaId,
      activeStatus: true,
    };
    CUSTOMER_APIs.getCustomerProfiles(args).then((res: any) => {
      const response: any = res.response;
      const { status } = res;
      if (status === 200) {
        let list = response.records.map((r) => {
          return {
            id: r.id,
            label: r.name,
            igst: r.isIgst,
          };
        });
        setParties(list);
        if (areaId == null) {
          getRows();
        }
      }
    });
  };

  const getRows = async () => {
    try {
      // const routeId = areasRef.current.selectedValue.id;
      const args = {
        // activeStatus: "true",
        // isSale: "true",
        // pageNo: 1,
        // pageLength: DEFAULTS.PAGE_LENGTH,
        // routeId,
      };
      setFilter(args);
      setRows(null);
      const res: any = await PRODUCT_APIs.getProductsByPrice(args);
      const { status, response } = res;
      console.log("res", res);
      if (status === 200) {
        let list = response.records;
        for (let i = 0; i < list.length; i++) {
          list[i]["slNo"] = i + 1;

          list[i]["stockQty"] = stocks[list[i].productSkuId]
            ? stocks[list[i].productSkuId]
            : 0;
          if (list[i].stockQty <= 0) {
            list[i]["_className"] = "stockHighlight";
          }
        }
        setRows(list);
        setLoading(false);
      }
    } catch (error) {
      console.error("getProductsSku:", error);
    }
  };

  // const mangeSalesInvoice = () => {
  //   // const orderNumber = (
  //   //   document.getElementById("orderNumber") as HTMLInputElement
  //   // ).value.trim();
  //   // const orderDate = (
  //   //   document.getElementById("orderDate") as HTMLInputElement
  //   // ).value.trim();
  //   // const areaId = areasRef.current.selectedValue.id;
  //   // const customerId = partyRef.current.selectedValue.id;
  //   // const vehicleId = vehicleRef.current.selectedValue.id;
  //   // const branchId = branchRef.current.selectedValue.id;
  //   // const stockLoadId = stockRef.current.selectedValue.id;
  //   // const productSkuId = productRef.current.selectedValue.id;
  //   // const schemeId = schemeRef.current.selectedValue.id;
  //   // const taxAmount = (
  //   //   document.getElementById("taxAmount") as HTMLInputElement
  //   // ).value.trim();
  //   // const total = (
  //   //   document.getElementById("total") as HTMLInputElement
  //   // ).value.trim();
  //   // const basic = (
  //   //   document.getElementById("basic") as HTMLInputElement
  //   // ).value.trim();
  //   // const remarks = (
  //   //   document.getElementById("remarks") as HTMLInputElement
  //   // ).value.trim();
  //   // const sold = (
  //   //   document.getElementById("sold") as HTMLInputElement
  //   // ).value.trim();
  //   // const soldSku = (
  //   //   document.getElementById("soldSku") as HTMLInputElement
  //   // ).value.trim();
  //   // const price = (
  //   //   document.getElementById("price") as HTMLInputElement
  //   // ).value.trim();
  //   // const orderItemBasic = (
  //   //   document.getElementById("orderItemBasic") as HTMLInputElement
  //   // ).value.trim();
  //   // const orderItemTaxAmount = (
  //   //   document.getElementById("orderItemTaxAmount") as HTMLInputElement
  //   // ).value.trim();
  //   // const orderItemTotal = (
  //   //   document.getElementById("orderItemTotal") as HTMLInputElement
  //   // ).value.trim();
  //   // const offers = (
  //   //   document.getElementById("offers") as HTMLInputElement
  //   // ).value.trim();
  //   // let ele = document.querySelector(
  //   //   'input[name="activeStatus"]:checked'
  //   // ) as HTMLInputElement;
  //   // const status = ele ? ele.value : "";

  //   const comments = (
  //     document.getElementById("comments") as HTMLInputElement
  //   ).value.trim();
  //   const areaId = areasRef.current.selectedValue.id;
  //   const partyId = partyRef.current.selectedValue.id;
  //   let rows = [];
  //   for (let i = 0; i < addSalesRef.getRowData().length; i++) {
  //     {
  //       addSalesRef.getRowData()[i].quantity
  //         ? (rows = addSalesRef.getRowData())
  //         : (rows = []);
  //     }
  //   }
  //   const summary = addSalesRef.getSummary();
  //   console.log("summary", rows);
  //   setLoading(true);

  //   const args = {
  //     orderId: -1,
  //     // orderNumber: "123567", ORDER NUMBER TP BE GENERATED IN BACKEND
  //     customerId: partyId, // SELECTEDpARTY.ID
  //     // orderDate: "2023-10-14 10:15:05", // BACKEND
  //     areaId: areaId, // BACKEND ENTRY
  //     taxAmount: Number(
  //       (
  //         Number(summary[0].cgst.replace("Total CGST : ", "")) +
  //         Number(summary[0].sgst.replace("Total SGST : ", ""))
  //       ).toFixed(2)
  //     ), // frontend
  //     // BASIC = QTY * RATE
  //     // TAX AMOUNT = BASIC * TAXPC/100
  //     // SGST = CGST = TAX AMOUNT/2
  //     isSale: "true",
  //     isReturn: "false",
  //     cgstAmount: Number(
  //       Number(summary[0].cgst.replace("Total CGST : ", "")).toFixed(2)
  //     ),
  //     sgstAmount: Number(
  //       Number(summary[0].sgst.replace("Total SGST : ", "")).toFixed(2)
  //     ),
  //     total: Number(
  //       Number(summary[0].total.replace("Total Amount: ", "")).toFixed(2)
  //     ), // BASIC + TAX AMOUNT
  //     basic: Number(
  //       Number(summary[0].basic.replace("Total Basic: ", "")).toFixed(2)
  //     ),
  //     status: 1, // SMALL INT
  //     remarks: comments, // TEXT BOX INPUT
  //     //vehicleId: 33, // DONT SEND
  //     // branchId: 15, // FOR NOW, WE NEED TO DO IT IN BACKEND
  //     // stockLoadId: 2, // NULL HANDLE IN BACKEND iF MANDATORY
  //     // productSkuId: rows.id,
  //     // sold, // soldsku * gram BACKEND
  //     // soldSku, // THIS iS QTY
  //     // price,
  //     // orderItemBasic,
  //     // orderItemTaxAmount,
  //     // orderItemTotal,
  //     items: rows.map((item) => {
  //       return {
  //         orderItemId: -1,
  //         productSkuId: item.id,
  //         soldSku: item.quantity,
  //         itemcgstAmount: Number(
  //           (
  //             (Number(item.price * item.quantity) *
  //               Number(item.taxStructure.replace("%", "") / 100)) /
  //             2
  //           ).toFixed(2)
  //         ),
  //         itemsgstAmount: Number(
  //           (
  //             (Number(item.price * item.quantity) *
  //               Number(item.taxStructure.replace("%", "") / 100)) /
  //             2
  //           ).toFixed(2)
  //         ),
  //         price: item.price,
  //         orderItemBasic: Number(item.price * item.quantity),
  //         orderItemTaxAmount: Number(
  //           Number(item.price * item.quantity) *
  //             Number(item.taxStructure.replace("%", "") / 100)
  //         ),
  //         orderItemTotal: Number(
  //           Number(item.price * item.quantity) +Select Party
  //             Number(
  //               Number(item.price * item.quantity) *
  //                 Number(item.taxStructure.replace("%", "") / 100)
  //             )
  //         ),
  //       };
  //     }),
  //     // photos: [],
  //     // schemeId: 5, // NOT TO BE HERE, BACKEND
  //     // offers, // NOT TO BE SENT HERE, BACKEND
  //   };

  //   let errors = [];
  //   if (comments == "") {
  //     errors.push({
  //       id: "comments",
  //       msg:
  //         "<span class='bold'>" +
  //         LABELS.COMMENTS +
  //         "</span> " +
  //         WARNINGS.IS_A_REQUIRED_FIELD,
  //     });
  //   }

  //   if (partyId === -1) {
  //     errors.push({
  //       id: "partyId",
  //       msg:
  //         "<span class='bold'>" +
  //         LABELS.PARTY +
  //         "</span> " +
  //         WARNINGS.IS_A_REQUIRED_FIELD,
  //     });
  //   }
  //   if (areaId === -1) {
  //     errors.push({
  //       id: "areaId",
  //       msg:
  //         "<span class='bold'>" +
  //         LABELS.ROUTE +
  //         "</span> " +
  //         WARNINGS.IS_A_REQUIRED_FIELD,
  //     });
  //   }
  //   if (!args.status) {
  //     errors.push({
  //       id: "status",
  //       msg:
  //         "<span class='bold'>" +
  //         LABELS.STATUS +
  //         "</span> " +
  //         WARNINGS.IS_A_REQUIRED_FIELD,
  //     });
  //   }
  //   if (args.items.length === 0) {
  //     errors.push({
  //       id: "items",
  //       msg: WARNINGS.ITEM_IS_EMPTY,
  //     });
  //   }

  //   setMandatoryResults(errors);
  //   if (errors.length > 0) {
  //     COMMON_JS.showNotify(
  //       props,
  //       NOTIFY.WARNING,
  //       WARNINGS.PLEASE_CHECK_FORM_ERRORS
  //     );
  //     return;
  //   }
  //   // setIsSubmit(true);
  //   console.log("args:", args);
  //   // 0ENO629836U0X7RM	manageDmsInvoice
  //   ORDER_APIs.manageOrder(args).then((res) => {
  //     // setIsSubmit(false);
  //     if (res.status === 200) {
  //       COMMON_JS.showNotify(props, NOTIFY.SUCCESS, SUCCESS.RECORD_SUCCESS);
  //       setSelectedRow({});
  //       setManageDialog(false);
  //       reloadGrid();
  //     } else {
  //       COMMON_JS.showNotify(props, NOTIFY.ERROR, ERRORS.RECORD_FAILED);
  //     }
  //   });
  // };

  const mangeSalesInvoice = () => {
    const areaId = areasRef.current.selectedValue.id;
    const partyId = partyRef.current.selectedValue.id;
    const partyIgst = partyRef.current.selectedValue.igst;
    const comments = (
      document.getElementById("comments") as HTMLInputElement
    ).value.trim();
    let gridRows = addSalesRef.getRowData();

    const summary = addSalesRef.getSummary();
    let items = [];
    let cgstAmt = 0;
    let sgstAmt = 0;
    let igstAmt = 0;
    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;

    for (let i = 0; i < gridRows.length; i++) {
      const row = gridRows[i];
      if (partyIgst === false) {
        cgstAmt = Number(
          (
            (Number(row.price * row.quantity) *
              Number(row.taxName.replace("%", "") / 100)) /
            2
          ).toFixed(2)
        );
        sgstAmt = Number(
          (
            (Number(row.price * row.quantity) *
              Number(row.taxName.replace("%", "") / 100)) /
            2
          ).toFixed(2)
        );
        totalCgst = Number(summary[0].cgst.replace("Total CGST :", ""));
        totalSgst = Number(summary[0].sgst.replace("Total SGST :", ""));
      } else {
        igstAmt = Number(
          (
            Number(row.price * row.quantity) *
            Number(row.taxName.replace("%", "") / 100)
          ).toFixed(2)
        );

        totalIgst = Number(
          (
            Number(summary[0].cgst.replace("Total CGST : ", "")) +
            Number(summary[0].sgst.replace("Total SGST : ", ""))
          ).toFixed(2)
        );
      }
      items.push({
        // orderId: row.orderId,
        orderItemId: -1,
        productSkuId: row.productSkuId,
        soldSku: row.quantity,
        taxPercent: row.taxName.replace("%", ""),
        grams: row.gramQty,
        price: row.price,

        cgstAmt: cgstAmt,
        sgstAmt: sgstAmt,
        igstAmt: igstAmt,
        total: Number(
          Number(row.price * row.quantity) +
            Number(
              Number(row.price * row.quantity) *
                Number(row.taxName.replace("%", "") / 100)
            )
        ),
        basic: Number(row.price * row.quantity),
        taxAmount: Number(
          Number(row.price * row.quantity) *
            Number(row.taxName.replace("%", "") / 100)
        ),
        sold: Number(row.quantity * row.gramQty),
      });
    }

    const args = {
      customerId: partyId,
      areaId: areaId,
      remarks: comments,
      rows: [
        {
          items: items,
          orderId: -1,
          totalTaxAmount: Number(
            (
              Number(summary[0].cgst.replace("Total CGST : ", "")) +
              Number(summary[0].sgst.replace("Total SGST : ", ""))
            ).toFixed(2)
          ),
          totalNetAmt: Number(summary[0].total.replace("Total Amount:", "")),
          totalBasic: Number(summary[0].basic.replace("Total Basic:", "")),
          totalCgst: totalCgst,
          totalSgst: totalSgst,
          totalIgst: totalIgst,
        },
      ],
    };
    console.log("args", args);

    ORDER_APIs.manageOrders(args).then((res) => {
      if (res.status === 200) {
        COMMON_JS.showNotify(props, NOTIFY.SUCCESS, SUCCESS.RECORD_SUCCESS);
        setSelectedRow({});
        reloadGrid();
        setManageDialog(false);
      } else {
        COMMON_JS.showNotify(props, NOTIFY.ERROR, ERRORS.RECORD_FAILED);
      }
    });
  };

  const handleAreaChange = async (e, f) => {
    if (f.id) {
      getParty(f.id);
    }
  };

  const reloadGrid = () => {
    setRows(null);
    getRows();
  };

  return (
    <React.Fragment>
      <div className="userDashboard">
        {!hideHeader && (
          <div className="pageInfo">
            <ul className="breadcrumbs">
              <li onClick={() => setPageMenu(LABELS.HOME)}>{LABELS.HOME}</li>
              <li className="dim">{" / "}</li>
              <li onClick={() => props.setPageMenu(LABELS.HOME)}>
                {LABELS.SALES}
              </li>
              <li className="dim">{" / "}</li>
              <li className="active">{TITLES.ADD_SALES_INVOICE}</li>
            </ul>
            <h4>{TITLES.ADD_SALES_INVOICE}</h4>
          </div>
        )}
        <div>
          <div className="filterBorder" style={{ width: "96%" }}>
            <UiAccordion id={"filter"} expanded={true}>
              <div className="flexRowAround">
                <div className="formGroup width30 required">
                  <label>
                    {LABELS.ROUTE} <span>{"*"}</span>
                  </label>
                  {areas ? (
                    <AutoComplete
                      list={areas}
                      childRef={areasRef}
                      onChange={handleAreaChange}
                      defaultValue={
                        selectedRow.id
                          ? { id: selectedRow.id, label: selectedRow.label }
                          : { id: -1, label: "Select Route" }
                      }
                    />
                  ) : (
                    <Skeleton height={45} width={"100%"} />
                  )}
                </div>
                <div className="formGroup width30 required">
                  <label>
                    {LABELS.PARTY}
                    <span>{"*"}</span>
                  </label>
                  {parties ? (
                    <AutoComplete
                      list={parties}
                      childRef={partyRef}
                      defaultValue={
                        selectedRow.id
                          ? { id: selectedRow.id, label: selectedRow.label }
                          : { id: -1, label: "Select Party" }
                      }
                    />
                  ) : (
                    <Skeleton height={45} width={"100%"} />
                  )}
                </div>
                <div className="formGroup width30 required">
                  <TxtRow
                    id={"comments"}
                    label={LABELS.COMMENTS}
                    required={true}
                    defaultValue={selectedRow.comment}
                  />
                </div>
              </div>
            </UiAccordion>
          </div>
          {rows ? (
            <>
              <div className="grid excelGridWrapper">
                <ExcelGrid
                  id={"addSalesInvoice"}
                  height={window.innerHeight * 0.5}
                  childRef={addSalesRef}
                  cols={columns}
                  rows={rows}
                  exportOption={true}
                  newRowOption={false}
                  exportName={LABELS.ADD_SALES_INVOICE}
                  onReload={reloadGrid}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "right",
                  width: "97%",
                }}
              >
                <div
                  style={{
                    width: "35%",
                    display: "flex",
                    justifyContent: "right",
                  }}
                >
                  <Btn
                    text={LABELS.SAVE}
                    onClick={mangeSalesInvoice}
                    marginRight={15}
                    bgColor={COLORS.GRID_ADD_NEW}
                    //  loading={isSubmit}
                  />
                </div>
              </div>
            </>
          ) : (
            <AddSalesInvoiceSkeleton />
          )}
        </div>
        {mandatoryResults.length > 0 && (
          <div className="mandatoryBox">
            <ul>
              {mandatoryResults.map((m) => {
                return (
                  <li>
                    <span dangerouslySetInnerHTML={{ __html: m.msg }} />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default withSnackbar(AddSalesInvoice);
